<script setup lang="ts">
import PtwResetFilterButton from '@/components/buttons/PtwResetFilterButton.vue';
import SearchField from '@/components/filter/SearchField.vue';
import Autocomplete from '@/components/forms/Autocomplete/Autocomplete.vue';
import Select from '@/components/forms/Select/Select.vue';

import { sortDown, sortUp } from '@/components/icons/icons';
import { SortItem } from '@/components/OverviewTable/types.ts';
import { useQaEquipmentStore } from '@/stores/qaEquipmentStore.ts';
import { getTranslatedOptions } from '@/utils/helper';
import { I18nKey } from '@/utils/typings';
import { QaEquipmentAddFilterAndSortingProps } from '../types';
import { uniq } from 'lodash';

const sortModel = defineModel<SortItem[]>('sort', {
  required: true,
});

const props = defineProps<QaEquipmentAddFilterAndSortingProps>();

const qaEquipmentStore = useQaEquipmentStore();

const brandOptions = getTranslatedOptions(
  uniq(
    props.items?.map((equipmentCatalogDto) => equipmentCatalogDto.brand1 ?? '').filter(Boolean),
  ) ?? [],
  'BrandEnum',
);
const subCategoryOptions = getTranslatedOptions(
  uniq(
    props.items
      ?.map((equipmentCatalogDto) => equipmentCatalogDto.subCategory ?? '')
      .filter(Boolean),
  ) ?? [],
  'QaEquipmentSubCategoryEnum',
);

const searchedFieldsI18nKeys: I18nKey[] = ['Search'];

const emits = defineEmits<{
  reset: [];
}>();

function clearFilters() {
  emits('reset');
}
</script>

<template>
  <v-row class="bg-secondary-lighten-5 align-center mt-2">
    <v-col cols="12" sm="3" md="2" lg="2">
      <SearchField
        v-model="qaEquipmentStore.addConfigurationFilterObject.search"
        :placeholder="
          $t('SearchPlaceholder', {
            columns: searchedFieldsI18nKeys.map((key) => `$t(${key})`),
          })
        "
      />
    </v-col>
    <v-col cols="12" sm="3" md="2" lg="2">
      <Autocomplete
        v-model="qaEquipmentStore.addConfigurationFilterObject.brand1"
        :label="$t('Brand')"
        :items="brandOptions"
        :placeholder="$t('All')"
        multiple
        clearable
        emptyAsUndefined
        chips
      />
    </v-col>
    <v-col cols="12" sm="3" md="2" lg="2">
      <Autocomplete
        v-model="qaEquipmentStore.addConfigurationFilterObject.subCategory"
        :label="$t('Type_one')"
        :items="subCategoryOptions"
        :placeholder="$t('All')"
        :maxNumDisplayItems="1"
        multiple
        clearable
        emptyAsUndefined
        chips
      />
    </v-col>
    <v-col cols="12" sm="3" md="2" lg="2" class="d-flex justify-start">
      <PtwResetFilterButton @click="clearFilters" />
    </v-col>
    <v-col class="d-flex justify-end">
      <Select
        :modelValue="sortModel[0].key"
        :label="$t('SortBy')"
        :items="sortByItems"
        data-testid="sortBySelect"
        style="min-width: 240px"
        @update:modelValue="
          (newKey) => {
            sortModel[0] = { ...sortModel[0], key: newKey };
          }
        "
      >
        <template #append>
          <TextVBtn
            color="secondary"
            size="small"
            variant="text"
            :icon="sortModel[0].order === 'desc' ? sortDown : sortUp"
            @click="
              () => {
                sortModel[0] = {
                  ...sortModel[0],
                  order:
                    sortModel[0].order === 'asc'
                      ? 'desc'
                      : sortModel[0].order === 'desc'
                        ? 'asc'
                        : 'desc',
                };
              }
            "
          />
        </template>
      </Select>
    </v-col>
  </v-row>
</template>
